import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, RotateCcw, CheckCircle2 } from "lucide-react";

// Records mic audio, draws a live waveform, computes duration + avg RMS amplitude.
export default function VoiceRecorder({ onComplete, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | recorded
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [levels, setLevels] = useState(new Array(40).fill(4));

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const rmsSumRef = useRef(0);
  const rmsCountRef = useRef(0);
  const timerRef = useRef(null);
  const blobRef = useRef(null);

  useEffect(() => () => cleanup(), []);

  function cleanup() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close();
  }

  async function start() {
    setError("");
    setSeconds(0);
    rmsSumRef.current = 0;
    rmsCountRef.current = 0;
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = finalize;
      mr.start();

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      draw();

      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setState("recording");
    } catch (e) {
      setError("Microphone access denied. Please allow mic permission and retry.");
    }
  }

  function draw() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    rmsSumRef.current += rms;
    rmsCountRef.current += 1;
    setLevels((prev) => {
      const next = prev.slice(1);
      next.push(Math.max(4, Math.min(48, rms * 400)));
      return next;
    });
    rafRef.current = requestAnimationFrame(draw);
  }

  function stop() {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  }

  function finalize() {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    blobRef.current = blob;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close();
    setState("recorded");
  }

  function reset() {
    blobRef.current = null;
    setSeconds(0);
    setLevels(new Array(40).fill(4));
    setState("idle");
  }

  function submit() {
    const avg = rmsCountRef.current ? rmsSumRef.current / rmsCountRef.current : 0;
    onComplete({ blob: blobRef.current, duration: seconds, avgAmplitude: Number(avg.toFixed(4)) });
  }

  return (
    <div className="flex flex-col items-center" data-testid="voice-recorder">
      {/* Waveform */}
      <div className="w-full max-w-md h-24 flex items-center justify-center gap-[3px] mb-6 rounded-xl bg-slate-50 border border-slate-200 px-4 overflow-hidden">
        {levels.map((h, i) => (
          <div
            key={i}
            className="w-1.5 rounded-full"
            style={{
              height: `${h}px`,
              background: state === "recording" ? "#3B82F6" : "#CBD5E1",
              transition: "height 0.08s ease",
            }}
          />
        ))}
      </div>

      <div className="mono text-2xl font-semibold text-ink mb-6">
        {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-4 text-center max-w-sm" data-testid="recorder-error">{error}</p>
      )}

      {state === "idle" && (
        <button
          onClick={start}
          disabled={disabled}
          data-testid="record-start-btn"
          className="relative h-20 w-20 rounded-full bg-brand text-white flex items-center justify-center shadow-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
        >
          <Mic className="h-8 w-8" />
        </button>
      )}

      {state === "recording" && (
        <button
          onClick={stop}
          data-testid="record-stop-btn"
          className="relative h-20 w-20 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg hover:bg-red-700 transition-colors"
        >
          <span className="absolute inset-0 rounded-full bg-red-500 animate-pulse-ring" />
          <Square className="h-7 w-7 relative fill-current" />
        </button>
      )}

      {state === "recorded" && (
        <div className="flex flex-col items-center gap-4 w-full max-w-md">
          <div className="flex items-center gap-2 text-brand font-medium">
            <CheckCircle2 className="h-5 w-5" /> Recording captured ({seconds}s)
          </div>
          <div className="flex gap-3">
            <button
              onClick={reset}
              data-testid="record-reset-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-ink-soft hover:bg-slate-50 transition-colors"
            >
              <RotateCcw className="h-4 w-4" /> Re-record
            </button>
            <button
              onClick={submit}
              data-testid="record-submit-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover transition-colors"
            >
              Run Quality Check & Analyse
            </button>
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-ink-muted text-center max-w-sm">
        Speak naturally for 5–30 seconds in any language. Raw acoustic features are never shown to you.
      </p>
    </div>
  );
}
